/**
 * Admin Experience Management Hook
 *
 * Fetches paginated experience list + stats, provides moderation action methods.
 *
 * API Endpoints:
 *   GET  /api/v1/admin/experiences?search=&moderation_status=&page=&limit=
 *   GET  /api/v1/admin/experiences/stats
 *   POST /api/v1/admin/experiences/:id/approve
 *   POST /api/v1/admin/experiences/:id/reject   { reason }
 *   POST /api/v1/admin/experiences/:id/flag      { reason }
 *   POST /api/v1/admin/experiences/:id/suspend   { reason }
 *   POST /api/v1/admin/experiences/:id/unflag
 *
 * Story: SCRUM-843
 */

import { useState, useEffect, useCallback } from 'react';
import apiRequest from '../../services/api';

export default function useAdminExperiences() {
  const [experiences, setExperiences] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    moderation_status: '',
    active: '',
    page: 1,
  });

  const fetchExperiences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.search) { params.set('search', filters.search); }
      if (filters.moderation_status) { params.set('moderation_status', filters.moderation_status); }
      if (filters.active !== '') { params.set('active', filters.active); }
      params.set('page', String(filters.page));
      params.set('limit', '25');

      const response = await apiRequest(`/api/v1/admin/experiences?${params.toString()}`);

      if (response.success) {
        setExperiences(response.data.experiences || []);
        setPagination(response.data.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
      }
    } catch (err) {
      setError(err.message || 'Failed to load experiences');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiRequest('/api/v1/admin/experiences/stats');
      if (response.success) {
        setStats(response.data || {});
      }
    } catch (_err) {
      // Stats failure is non-critical
    }
  }, []);

  useEffect(() => {
    fetchExperiences();
  }, [fetchExperiences]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ── Moderation Actions ──────────────────────────────────────────────

  const approveExperience = useCallback(async (experienceId) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`/api/v1/admin/experiences/${experienceId}/approve`, {
        method: 'POST',
      });
      if (response.success) {
        await fetchExperiences();
        await fetchStats();
      }
      return response;
    } finally {
      setActionLoading(false);
    }
  }, [fetchExperiences, fetchStats]);

  const rejectExperience = useCallback(async (experienceId, reason) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`/api/v1/admin/experiences/${experienceId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      if (response.success) {
        await fetchExperiences();
        await fetchStats();
      }
      return response;
    } finally {
      setActionLoading(false);
    }
  }, [fetchExperiences, fetchStats]);

  const flagExperience = useCallback(async (experienceId, reason) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`/api/v1/admin/experiences/${experienceId}/flag`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      if (response.success) {
        await fetchExperiences();
        await fetchStats();
      }
      return response;
    } finally {
      setActionLoading(false);
    }
  }, [fetchExperiences, fetchStats]);

  const suspendExperience = useCallback(async (experienceId, reason) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`/api/v1/admin/experiences/${experienceId}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      if (response.success) {
        await fetchExperiences();
        await fetchStats();
      }
      return response;
    } finally {
      setActionLoading(false);
    }
  }, [fetchExperiences, fetchStats]);

  const unflagExperience = useCallback(async (experienceId) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`/api/v1/admin/experiences/${experienceId}/unflag`, {
        method: 'POST',
      });
      if (response.success) {
        await fetchExperiences();
        await fetchStats();
      }
      return response;
    } finally {
      setActionLoading(false);
    }
  }, [fetchExperiences, fetchStats]);

  return {
    experiences,
    pagination,
    stats,
    loading,
    error,
    actionLoading,
    filters,
    setFilters,
    refresh: useCallback(() => { fetchExperiences(); fetchStats(); }, [fetchExperiences, fetchStats]),
    approveExperience,
    rejectExperience,
    flagExperience,
    suspendExperience,
    unflagExperience,
  };
}
